import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Bar, Doughnut } from 'react-chartjs-2';
import { CheckCircle, Clock, DollarSign, FileText } from 'lucide-react';
import StatCard from '../components/StatCard';
import MiniStatCard from '../components/MiniStatCard';
import RankingTable, { Td, TdBold, TdSmall, Badge } from '../components/RankingTable';
import CardSkeleton, { MiniCardSkeleton } from '../components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import dashboardStatistics from 'api/requests/dashboardStatistics';
import { isIncrease, marginText, outcomeOf, percent } from '../expenseFormat';

const CATEGORY_COLORS = ['#8025e8', '#4F86F7', '#2BC866', '#ff9123', '#E7505A', '#14B8A6', '#94A3B8'];

const STATUS = {
    0: { label: 'Pendente', bg: '#FFF1E0', color: '#C26A00' },
    1: { label: 'Pago', bg: '#E3F8EB', color: '#1B8A4B' },
};

const eventColumns = [
    { label: 'Evento' },
    { label: 'Despesas', align: 'right' },
    { label: 'Receita líquida', align: 'right' },
    { label: 'Resultado', align: 'right' },
    { label: 'Margem', align: 'right' },
];

const axisTicks = {
    beginAtZero: true,
    fontColor: '#999',
    fontSize: 12,
    callback: (value) => (value >= 1000 ? `${value / 1000}k` : value),
};

const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
        yAxes: [{ ticks: axisTicks, gridLines: { color: '#f0f0f0', drawBorder: false } }],
        xAxes: [{ ticks: { fontColor: '#999', fontSize: 12 }, gridLines: { display: false } }],
    },
    legend: { position: 'top', align: 'end', labels: { boxWidth: 12, fontSize: 12 } },
    tooltips: {
        backgroundColor: '#1A1A1A',
        cornerRadius: 8,
        xPadding: 12,
        yPadding: 10,
        callbacks: {
            label: (tooltipItem, data) => ` ${data.datasets[tooltipItem.datasetIndex].label}: ${currency.format(tooltipItem.yLabel)}`,
        },
    },
};

const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 65,
    legend: { display: false },
    tooltips: {
        backgroundColor: '#1A1A1A',
        cornerRadius: 8,
        callbacks: {
            label: (tooltipItem, data) => ` ${data.labels[tooltipItem.index]}: ${currency.format(data.datasets[0].data[tooltipItem.index])}`,
        },
    },
};

export default function DespesasTab({ periodParams }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!periodParams) return;

        setIsLoading(true);
        setHasError(false);
        dashboardStatistics.getDespesas(periodParams)
            .then(response => setData(response.data))
            .catch(() => setHasError(true))
            .finally(() => setIsLoading(false));
    }, [periodParams]);

    if (isLoading) {
        return (
            <Container>
                <CardsGrid>
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                </CardsGrid>
                <TwoColumns>
                    <CardSkeleton height={320} />
                    <CardSkeleton height={320} />
                </TwoColumns>
            </Container>
        );
    }

    if (hasError) {
        return <Notice>Não foi possível carregar as despesas agora. Tente de novo em instantes.</Notice>;
    }

    if (!data) return null;

    const { resumo, resultado, porMes, porCategoria, porEvento, destaques, maioresDespesas, fornecedores } = data;
    const outcome = outcomeOf(resultado.desfecho);
    const OutcomeIcon = outcome.Icon;
    const hasSpending = resumo.quantidade > 0 || resumo.orcamento > 0;
    const biggest = Math.max(resultado.receitaLiquida, resultado.despesas);

    const monthlyChartData = {
        labels: porMes.labels,
        datasets: [
            {
                label: 'Receita líquida',
                data: porMes.receita,
                backgroundColor: 'rgba(43, 200, 102, 0.7)',
                borderColor: '#2BC866',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
            {
                label: 'Despesas',
                data: porMes.despesas,
                backgroundColor: 'rgba(255, 145, 35, 0.75)',
                borderColor: '#ff9123',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
        ],
    };

    const categoryColor = (index) => CATEGORY_COLORS[index % CATEGORY_COLORS.length];

    const categoryChartData = {
        labels: porCategoria.map(categoria => categoria.nome),
        datasets: [{
            data: porCategoria.map(categoria => categoria.total),
            backgroundColor: porCategoria.map((_, index) => categoryColor(index)),
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: '#fff',
        }],
    };

    return (
        <Container>
            <CardsGrid>
                <MiniStatCard
                    label="Total gasto"
                    value={currency.format(resumo.gasto)}
                    trend={resumo.gastoTrend}
                    caption={`${resumo.quantidade} ${resumo.quantidade === 1 ? 'despesa' : 'despesas'} entre pagas e pendentes`}
                    isPositive={!isIncrease(resumo.gastoTrend)}
                    icon={DollarSign}
                />
                <MiniStatCard
                    label="Pago"
                    value={currency.format(resumo.pago)}
                    caption={resumo.percentualPago === null ? 'Nada gasto ainda' : `${percent(resumo.percentualPago)} do que foi gasto`}
                    icon={CheckCircle}
                />
                <MiniStatCard
                    label="Falta pagar"
                    value={currency.format(resumo.pendente)}
                    caption={pendingCaption(resumo)}
                    icon={Clock}
                />
                <MiniStatCard
                    label="Orçamentos"
                    value={currency.format(resumo.orcamento)}
                    caption="Ainda não contam como gasto"
                    icon={FileText}
                />
            </CardsGrid>

            {!hasSpending ? (
                <Notice>
                    Nenhuma despesa neste período. Lance as despesas de cada evento em Eventos, na aba Despesas, e elas aparecem aqui.
                </Notice>
            ) : (
                <>
                    <ResultRow>
                        <StatCard title="Resultado do período" subtitle="Receita líquida (já sem as taxas) menos despesas">
                            <OutcomeHead color={outcome.color}>
                                <OutcomeIcon size={18} color={outcome.color} />
                                <span>{outcome.label}</span>
                            </OutcomeHead>
                            {resultado.desfecho !== 'break_even' && (
                                <OutcomeValue color={outcome.color}>{currency.format(Math.abs(resultado.saldo))}</OutcomeValue>
                            )}
                            <Caption>{marginText(resultado.margem)}</Caption>

                            <Bars>
                                <BarLine label="Receita líquida" value={resultado.receitaLiquida} biggest={biggest} color={colors.successDark} />
                                <BarLine label="Despesas" value={resultado.despesas} biggest={biggest} color={colors.dangerDark} />
                            </Bars>

                            {resultado.comprometimento !== null && (
                                <Caption>As despesas consomem {percent(resultado.comprometimento)} da receita líquida.</Caption>
                            )}
                            {resultado.taxas > 0 && (
                                <Caption>Taxas retidas no período: {currency.format(resultado.taxas)}.</Caption>
                            )}

                            {(destaques.maiorLucro || destaques.maiorPrejuizo) && (
                                <Highlights>
                                    {destaques.maiorLucro && (
                                        <Highlight>
                                            <HighlightLabel>Maior lucro</HighlightLabel>
                                            <HighlightName>{destaques.maiorLucro.nome}</HighlightName>
                                            <Amount>{currency.format(destaques.maiorLucro.valor)}</Amount>
                                        </Highlight>
                                    )}
                                    {destaques.maiorPrejuizo && (
                                        <Highlight>
                                            <HighlightLabel>Maior prejuízo</HighlightLabel>
                                            <HighlightName>{destaques.maiorPrejuizo.nome}</HighlightName>
                                            <Amount negative>{currency.format(destaques.maiorPrejuizo.valor)}</Amount>
                                        </Highlight>
                                    )}
                                </Highlights>
                            )}
                        </StatCard>

                        <StatCard title="Receita líquida vs Despesas" subtitle="Mês a mês, nos eventos do período">
                            <ChartContainer>
                                <Bar data={monthlyChartData} options={barOptions} />
                            </ChartContainer>
                        </StatCard>
                    </ResultRow>

                    <TwoColumns>
                        <StatCard title="Despesas por categoria" subtitle="Para onde foi o dinheiro">
                            {porCategoria.length === 0 ? (
                                <Empty>Nenhuma despesa paga ou pendente neste período.</Empty>
                            ) : (
                                <CategoryContent>
                                    <DoughnutWrap>
                                        <Doughnut data={categoryChartData} options={doughnutOptions} />
                                    </DoughnutWrap>
                                    <LegendList>
                                        {porCategoria.map((categoria, index) => (
                                            <LegendItem key={categoria.nome}>
                                                <LegendLeft>
                                                    <LegendDot color={categoryColor(index)} />
                                                    <span>{categoria.nome}</span>
                                                </LegendLeft>
                                                <LegendValue>
                                                    {currency.format(categoria.total)} <Muted>{percent(categoria.percentual)}</Muted>
                                                </LegendValue>
                                            </LegendItem>
                                        ))}
                                    </LegendList>
                                </CategoryContent>
                            )}
                        </StatCard>

                        <StatCard title="Maiores despesas" subtitle="Os lançamentos mais caros do período">
                            {maioresDespesas.length === 0 ? (
                                <Empty>Nenhuma despesa paga ou pendente neste período.</Empty>
                            ) : (
                                maioresDespesas.map((despesa, index) => (
                                    <ListItem key={`${despesa.nome}-${index}`}>
                                        <div>
                                            <ItemName>{despesa.nome}</ItemName>
                                            <ItemSub>
                                                {despesa.evento} • {despesa.categoria}
                                                {despesa.data ? ` • ${dayjs(despesa.data).format('DD/MM/YYYY')}` : ''}
                                            </ItemSub>
                                        </div>
                                        <ItemRight>
                                            <ItemValue>{currency.format(despesa.valor)}</ItemValue>
                                            {STATUS[despesa.status] && (
                                                <Badge bg={STATUS[despesa.status].bg} color={STATUS[despesa.status].color}>
                                                    {STATUS[despesa.status].label}
                                                </Badge>
                                            )}
                                        </ItemRight>
                                    </ListItem>
                                ))
                            )}
                        </StatCard>
                    </TwoColumns>

                    <TwoColumns wide>
                        <StatCard title="Resultado por evento" subtitle="Quem mais gastou no período e o que sobrou">
                            <TableScroll>
                                <RankingTable
                                    columns={eventColumns}
                                    data={porEvento}
                                    renderRow={(evento) => (
                                        <tr key={evento.id}>
                                            <TdBold>{evento.nome}</TdBold>
                                            <Td align="right">{currency.format(evento.despesas)}</Td>
                                            <Td align="right">{currency.format(evento.receita)}</Td>
                                            <Td align="right">
                                                <Amount negative={evento.resultado < 0}>{currency.format(evento.resultado)}</Amount>
                                            </Td>
                                            <Td align="right">{evento.margem === null ? '-' : percent(evento.margem)}</Td>
                                        </tr>
                                    )}
                                />
                            </TableScroll>
                        </StatCard>

                        <StatCard title="Principais fornecedores" subtitle="Para quem mais se pagou">
                            {fornecedores.length === 0 ? (
                                <Empty>Informe o fornecedor ao lançar uma despesa para ver o ranking aqui.</Empty>
                            ) : (
                                fornecedores.map((fornecedor) => (
                                    <ListItem key={fornecedor.nome}>
                                        <div>
                                            <ItemName>{fornecedor.nome}</ItemName>
                                            <ItemSub>{fornecedor.itens} {fornecedor.itens === 1 ? 'lançamento' : 'lançamentos'}</ItemSub>
                                        </div>
                                        <ItemValue>{currency.format(fornecedor.total)}</ItemValue>
                                    </ListItem>
                                ))
                            )}
                        </StatCard>
                    </TwoColumns>
                </>
            )}
        </Container>
    );
}

/** "Falta pagar" avisa das pendentes cuja data já passou. */
function pendingCaption({ pendente, pendentesAtrasadas }) {
    if (pendente === 0) return 'Nada por pagar';

    if (pendentesAtrasadas.itens === 0) return 'Nenhuma com data já passada';

    const plural = pendentesAtrasadas.itens === 1 ? 'despesa com data já passada' : 'despesas com data já passada';

    return `${pendentesAtrasadas.itens} ${plural} (${currency.format(pendentesAtrasadas.total)})`;
}

function BarLine({ label, value, biggest, color }) {
    const width = value > 0 && biggest > 0 ? Math.max((value / biggest) * 100, 2) : 0;

    return (
        <BarRow>
            <BarLabel>
                <span>{label}</span>
                <strong>{currency.format(value)}</strong>
            </BarLabel>
            <BarTrack>
                <BarFill width={width} color={color} />
            </BarTrack>
        </BarRow>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const ResultRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 20px;

    /* O item da grade não encolhe abaixo do conteúdo: sem isso a tabela estoura o cartão em vez de rolar. */
    & > * {
        min-width: 0;
    }

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const TwoColumns = styled.div`
    display: grid;
    grid-template-columns: ${({ wide }) => (wide ? '2fr 1fr' : '1fr 1fr')};
    gap: 20px;

    /* O item da grade não encolhe abaixo do conteúdo: sem isso a tabela estoura o cartão em vez de rolar. */
    & > * {
        min-width: 0;
    }

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const Notice = styled.div`
    padding: 28px 24px;
    text-align: center;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px dashed ${colors.night}33;
    color: ${colors.night};
    font-size: 14px;
`;

const Empty = styled.p`
    margin: 0;
    padding: 12px 0;
    color: ${colors.night};
    font-size: 13px;
`;

const OutcomeHead = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${({ color }) => color};
`;

const OutcomeValue = styled.div`
    margin: 6px 0 2px;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ color }) => color};
`;

const Caption = styled.p`
    margin: 6px 0 0;
    font-size: 13px;
    color: ${colors.night};
`;

const Bars = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 18px;
`;

const BarRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BarLabel = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: ${colors.nightDark};
`;

const BarTrack = styled.div`
    width: 100%;
    height: 10px;
    border-radius: 5px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const BarFill = styled.div`
    height: 100%;
    border-radius: 5px;
    width: ${({ width }) => width}%;
    background: ${({ color }) => color};
`;

const Highlights = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #f0f0f0;
`;

const Highlight = styled.div``;

const HighlightLabel = styled.div`
    font-size: 12px;
    color: ${colors.night};
    font-weight: 500;
`;

const HighlightName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Amount = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ negative }) => (negative ? colors.dangerDark : colors.successDark)};
`;

const ChartContainer = styled.div`
    height: 300px;
    width: 100%;
`;

const CategoryContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
`;

const DoughnutWrap = styled.div`
    width: 180px;
    height: 180px;
`;

const LegendList = styled.div`
    width: 100%;
`;

const LegendItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    font-size: 14px;
    color: ${colors.nightDark};
`;

const LegendLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LegendDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;

const LegendValue = styled.span`
    font-weight: 600;
`;

const Muted = styled.span`
    font-weight: 500;
    color: ${colors.night};
`;

const ListItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
`;

const ItemName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ItemSub = styled(TdSmall)`
    margin-top: 3px;
`;

const ItemRight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
`;

const ItemValue = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
`;

const TableScroll = styled.div`
    overflow-x: auto;
`;
